// TODO This is a temporary workaround for the SVG to PNG conversion, ideally should be SVG
export const replaceSvgsWithImages = async (container: HTMLElement): Promise<void> => {
    const svgs = container.querySelectorAll('svg');
    await Promise.all(
        Array.from(svgs).map(async (svg) => {
            const imgSrc = await svgToPng(svg);
            const img = new Image();
            img.src = imgSrc;
            img.width = svg.clientWidth;
            img.height = svg.clientHeight;
            svg.replaceWith(img);
        })
    );
};

// TODO Only necessary on localhost, to be removed
export const replaceImgsWithProxyURL = async (container: HTMLElement): Promise<void> => {
    const imgs = container.querySelectorAll('img');
    await Promise.all(
        Array.from(imgs).map(async (img) => {
            // eslint-disable-next-line no-param-reassign
            if (!img.src.startsWith('data:image/png;base64,')) img.src = `https://api.allorigins.win/raw?url=${img.src}`;
        })
    );
};

// Emoji handling utilities
export const processEmoji = {
    // Regex for matching emojis and common symbols
    // Includes:
    // - Basic symbols (⚠️, ℹ️, etc): \u2000-\u2BFF -> [\u2000-\u2BFF][\uFE00-\uFE0F]?| NO
    // - Emojis: \u{1F300}-\u{1F9FF}
    // - Variation Selectors (for emoji styling): \uFE00-\uFE0F
    // - Zero Width Joiner (for compound emojis): \u200D
    regex: /[\u{1F300}-\u{1F9FF}][\uFE00-\uFE0F]?|[\u{1F300}-\u{1F9FF}]\u200D[\u{1F300}-\u{1F9FF}][\uFE00-\uFE0F]?/gu,

    // Create a canvas with emoji
    createCanvas(emoji: string, parentElement: HTMLElement) {
        const computedStyle = window.getComputedStyle(parentElement);
        const fontSize = parseInt(computedStyle.fontSize, 10);
        const emojiSize = Math.round(fontSize * 1.2); // 20% larger than text
        const size = emojiSize * 2; // Double size for better quality

        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.font = `${size}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(emoji, size / 2, size / 2 + 3);
        }

        return { canvas, emojiSize };
    },

    // Create an image element from canvas
    createImageElement(canvas: HTMLCanvasElement, emojiSize: number) {
        const img = document.createElement('img');
        img.src = canvas.toDataURL('image/png');
        img.style.width = `${emojiSize}px`;
        img.style.height = `${emojiSize}px`;
        img.style.verticalAlign = 'middle';
        img.style.display = 'inline-block';
        img.style.margin = '0 1px';
        return img;
    },

    // Process a text node and replace emojis with images
    processTextNode(node: ChildNode) {
        const text = node.textContent;
        const parentElement = node.parentElement;
        const parentNode = node.parentNode;
        if (!text || !parentElement || !parentNode) return;

        const matches = text.match(this.regex);
        if (!matches) return;

        // Create document fragment for new content
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;

        matches.forEach((emoji) => {
            // Add text before emoji
            const textBefore = text.slice(lastIndex, text.indexOf(emoji, lastIndex));
            if (textBefore) {
                fragment.appendChild(document.createTextNode(textBefore));
            }

            // Add emoji image
            const { canvas, emojiSize } = this.createCanvas(emoji, parentElement);
            const img = this.createImageElement(canvas, emojiSize);
            fragment.appendChild(img);

            lastIndex = text.indexOf(emoji, lastIndex) + emoji.length;
        });

        // Add remaining text
        if (lastIndex < text.length) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }

        parentNode.replaceChild(fragment, node);
    },
};

export const rgbaToRgb = (rgba: string, background = '#FFFFFF'): string => {
    // Match RGBA values
    const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*(\d?.\d+)?\)/);
    if (!match) return rgba; // Return original if not an RGBA color

    // eslint-disable-next-line prefer-const
    let [, r, g, b, a] = match.map(Number);
    a = a !== undefined ? a : 1; // Default alpha to 1 if missing

    // Convert background hex to RGB
    const hexToRgb = (hex: string): [number, number, number] => {
        // eslint-disable-next-line no-param-reassign
        hex = hex.replace(/^#/, '');
        if (hex.length === 3) {
            // eslint-disable-next-line no-param-reassign
            hex = hex
                .split('')
                .map((x) => x + x)
                .join('');
        }
        const bigint = parseInt(hex, 16);
        // eslint-disable-next-line no-bitwise
        return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
    };

    const [br, bg, bb] = hexToRgb(background);

    // Blend the colors
    const newR = Math.round(r * a + br * (1 - a));
    const newG = Math.round(g * a + bg * (1 - a));
    const newB = Math.round(b * a + bb * (1 - a));

    return `rgb(${newR}, ${newG}, ${newB})`; // Return solid RGB color
};

const inlineSvgStyles = (svgElement: SVGElement): string => {
    const clonedSvg = svgElement.cloneNode(true) as SVGElement;
    const styleSheets = Array.from(document.styleSheets) as CSSStyleSheet[];

    // Loop through all stylesheets and extract rules for SVG
    styleSheets.forEach((sheet) => {
        try {
            Array.from(sheet.cssRules).forEach((rule) => {
                if (rule instanceof CSSStyleRule && svgElement.matches(rule.selectorText)) {
                    const style = clonedSvg.style;
                    style.cssText += rule.style.cssText; // Apply styles inline
                }
            });
        } catch (e) {
            console.warn('Error reading CSS rules: ', e);
        }
    });

    return new XMLSerializer().serializeToString(clonedSvg);
};

const svgToPng = (svgElement: SVGElement): Promise<string> =>
    new Promise((resolve) => {
        const xml = inlineSvgStyles(svgElement);
        const svgBlob = new Blob([xml], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = svgElement.clientWidth || 300;
            canvas.height = svgElement.clientHeight || 300;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                console.error('Canvas context is not available');
                return;
            }
            ctx.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
            resolve(canvas.toDataURL('image/png'));
        };
        img.src = url;
    });
